import { Button, Checkbox, FormControl, HStack, Spinner, VStack } from '@chakra-ui/react';
import { SelfAccreditationClaims } from '@common/models/InvestorAccountModel/types';
import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { ROUTES } from 'src/apps/invest/config';
import { useVerifyAccreditationViewModel } from '../../models/VerifyAccreditationViewModel';

const defaultAccrediation = {
  certification: false,
  employment: false,
  income: false,
  netWorth: false,
  none: false,
};

export const AccreditationForm: React.FC = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [claims, setClaims] = useState<SelfAccreditationClaims>(defaultAccrediation);
  const hasMadeClaim = claims.certification || claims.employment || claims.income || claims.netWorth || claims.none;
  const viewModel = useVerifyAccreditationViewModel();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!claims) return;
    setIsSaving(true);
    const result = await viewModel.handleForm(claims);
    if (result.error) {
      navigate(ROUTES.error + '?error=' + result.error);
    } else {
      viewModel.setPage('success');
    }
    setIsSaving(false);
  };

  const onChange = (claim: keyof SelfAccreditationClaims) => {
    if (claim === 'none') {
      setClaims({ certification: false, employment: false, income: false, netWorth: false, none: !claims.none });
    } else {
      const newClaims = { ...claims, none: false };
      newClaims[claim] = !claims[claim];
      setClaims(newClaims);
    }
  };

  if (isSaving) return <Spinner margin={20} />;

  return (
    <VStack as="form" gap="40px" onSubmit={handleSubmit}>
      <FormControl>
        <VStack w={'100%'} letterSpacing="0.5px" fontWeight="600" gap="6px" alignItems="flex-start">
          <HStack
            cursor="pointer"
            onClick={() => {
              onChange('none');
            }}
            gap="20px"
            alignItems="flex-start">
            <Checkbox pointerEvents={'none'} isChecked={claims.none} mt="4px" />
            <TextBlock>No, I am not an accredited investor.</TextBlock>
          </HStack>
          <HStack
            cursor="pointer"
            onClick={() => {
              onChange('income');
            }}
            gap="20px"
            alignItems="flex-start">
            <Checkbox pointerEvents={'none'} isChecked={claims.income} mt="4px" />
            <TextBlock>
              Yes, I am an individual who has an income greater than $200,000 in each of the past two years or whose
              joint income with a spouse is greater than $300,000 for those years, and a reasonable expectation of the
              same income level in the current year.
            </TextBlock>
          </HStack>
          <HStack
            cursor="pointer"
            onClick={() => {
              onChange('netWorth');
            }}
            gap="20px"
            alignItems="flex-start">
            <Checkbox pointerEvents={'none'} isChecked={claims.netWorth} mt="4px" />
            <TextBlock>
              Yes, I am an individual who has a net worth, or joint net worth with the person’s spouse, that exceeds $1
              million at the time of the investment (The net worth amount cannot include the value of the person's
              primary residence.)
            </TextBlock>
          </HStack>
          <HStack
            cursor="pointer"
            onClick={() => {
              onChange('certification');
            }}
            gap="20px"
            alignItems="flex-start">
            <Checkbox pointerEvents={'none'} isChecked={claims.certification} mt="4px" />
            <TextBlock>
              Yes, I am an individual who hold certain certificates, designations, or credentials, such as Series 7,
              Series 65, and Series 82 licenses.
            </TextBlock>
          </HStack>
          <HStack
            cursor="pointer"
            onClick={() => {
              onChange('employment');
            }}
            gap="20px"
            alignItems="flex-start">
            <Checkbox pointerEvents={'none'} isChecked={claims.employment} mt="4px" />
            <TextBlock>
              Yes, I am an individual who is a "knowledgeable employee" and affiliated with the offerings on the
              platform.
            </TextBlock>
          </HStack>
        </VStack>
      </FormControl>
      <Button isDisabled={!hasMadeClaim} type="submit" w="full" p={7}>
        Verify
      </Button>
    </VStack>
  );
};
